import { useQuery } from '@tanstack/react-query';
import { requestTyped } from '@xdocs/ws-protocol';
import type { ErrorAlert, ReferralStats } from '@xdocs/schemas';
import { useWs } from '../../../ws/WsProvider.js';

export const REFERRAL_STATS_QUERY_KEY = ['referrals', 'stats'] as const;

export interface UseReferralStatsResult {
  stats: ReferralStats | undefined;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
}

/** `referral.stats` (F72) — the account-wide Invited/Joined/Credits-earned
 * tiles on `/settings/referrals`. Deliberately its OWN query, independent of
 * `useReferralList` — a failure here must degrade only the stat tiles, never
 * the `<ReferralPanel>` list below it (resilience.md: "degrade, don't crash"). */
export function useReferralStats(): UseReferralStatsResult {
  const { wsClient, connectionState } = useWs();
  const enabled = connectionState === 'connected';

  const query = useQuery<ReferralStats, ErrorAlert>({
    queryKey: REFERRAL_STATS_QUERY_KEY,
    queryFn: () => requestTyped(wsClient, 'referral.stats', {}),
    enabled,
  });

  return {
    stats: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    onRetry: () => void query.refetch(),
  };
}
